import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CONTRACT_STATUSES,
  formatHours,
  formatRelative,
  type ContractStatus,
  type DashboardResponse,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { contractPath, customerPath } from '@/lib/paths';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Progress,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  /*
   * Simpelt søjlediagram i ren CSS. To diagrammer retfærdiggør ikke et charting-bibliotek: det
   * ville være den største enkeltpost i frontend-bundlen for noget der er otte div'er og en
   * procentudregning. Delt med serviceaftalens Timer-fane, se components/domain.tsx.
   */
  BarChart,
  ContractStatusBadge,
  dsoHint,
  EmptyState,
  MetricCard,
  Money,
  PageHeader,
  Percent,
  SeverityBadge,
} from '@/components/domain';

/**
 * Dashboardets status er typet `string` og ikke `ContractStatus` (se DashboardResponse i
 * packages/shared): raekkerne kommer live fra Dynamics, som kan svare med en vaerdi der
 * ikke er i vores enum. Er den kendt, faar den samme badge som paa /kontrakter; ellers
 * staar den raa tekst, frem for et badge uden tone og uden dansk label.
 */
function isContractStatus(status: string): status is ContractStatus {
  return (CONTRACT_STATUSES as readonly string[]).includes(status);
}

export function DashboardPage() {
  /*
   * The dashboard shows the same to everyone with 'Se dashboard'. What a role cannot open is
   * shown as text, not as a link: a row or a "Se alle" that ends on /forbudt is worse than none.
   */
  const { can, canSeeReport, user } = useAuth();
  const opensContracts = can('contracts.view');
  const opensCustomers = can('customers.view');
  const seesAllAlerts = can('alerts.view_all');
  /* Your own alarm opens with 'Se mine alarmer', anyone else's with 'Se alle alarmer'. */
  const opensAlert = (recipientId: string | null | undefined): boolean =>
    seesAllAlerts || (can('alerts.view_own') && Boolean(recipientId) && recipientId === user?.id);
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardResponse>('/dashboard'),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !data) {
    return <Alert tone="error">Kunne ikke hente dashboardet. Prøv at genindlæse siden.</Alert>;
  }

  const { kpis, open_alerts, recent_contracts, recent_alerts, billing_by_month, top_customers } =
    data;
  const needsAttention = open_alerts.critical + open_alerts.warning;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          data.detection_last_run_at
            ? `Detektering kørt ${formatRelative(data.detection_last_run_at)}`
            : 'Detekteringsmotoren har ikke kørt endnu'
        }
      />

      {needsAttention > 0 ? (
        <Alert tone={open_alerts.critical > 0 ? 'error' : 'warning'} className="mb-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {open_alerts.critical > 0
                ? `${open_alerts.critical} kritiske alarmer kræver handling nu.`
                : `${open_alerts.warning} advarsler venter.`}
            </span>
            {/* Tallet er alles alarmer, saa listen bag knappen skal ogsaa vaere det. */}
            {seesAllAlerts ? (
              <Link to="/alarmer?recipient=all">
                <Button size="sm" variant="secondary">
                  Gennemgå alarmer
                </Button>
              </Link>
            ) : null}
          </div>
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Aktive kontrakter"
          value={kpis.active_contracts}
          hint={`${kpis.active_contracts_delta} nye seneste 30 dage`}
        />
        <MetricCard
          label="Faktureret i år"
          value={<Money value={kpis.billed_ytd_excl_vat} decimals={0} />}
          hint={
            <>
              <Percent value={kpis.billed_ytd_pct_of_contract_value} /> af aktiv kontraktværdi
            </>
          }
        />
        <MetricCard
          label="Ufaktureret WIP"
          value={<Money value={kpis.unbilled_wip_excl_vat} decimals={0} />}
          hint={`${formatHours(kpis.unbilled_wip_hours)} timer`}
          tone={kpis.unbilled_wip_excl_vat > 0 ? 'warning' : 'neutral'}
        />
        <MetricCard
          label="DSO"
          value={kpis.dso_days === null ? '—' : `${Math.round(kpis.dso_days)} dage`}
          // Faktisk betalingstid fra udligningerne i BC. Kan de ikke læses, er tallet et estimat,
          // og mærkningen skal med, så det ikke citeres som noget, det ikke er. Se OQ-10.
          hint={dsoHint(kpis.dso_is_estimated, kpis.dso_payment_count)}
        />
      </div>

      {/*
        Rækkens højde er sat her og kommer ikke fra indholdet.

        De to kort ligger i den samme gridrække, og en gridrække er lige så høj som sit
        højeste kort. Uden en fast højde bestemmer det kort, der tilfældigvis har mest
        indhold, hvor høj rækken bliver — og det andet står med et hul i bunden. Hvem der er
        højest skifter med data: ti alarmer er højere end ti kontraktrækker, ingen alarmer er
        meget lavere. Panelerne ville altså skifte facon hen over dagen.

        Derfor `auto-rows-[32rem]`: rækken er 512 px, hver liste fylder sin egen spalte og
        ruller det, der ikke er plads til. Kun fra `lg`, hvor de to står side om side — under
        det er gridden én kolonne, hver liste sin egen række, og en rulleflade inde i en side
        der også ruller, er værre end en lang liste.
      */}
      <div className="mt-5 grid gap-4 lg:auto-rows-[32rem] lg:grid-cols-3">
        <Card className="flex flex-col overflow-hidden lg:col-span-2">
          <CardHeader className="flex shrink-0 items-center justify-between">
            <CardTitle>Seneste kontrakter</CardTitle>
            {opensContracts ? (
              <Link to="/kontrakter" className="text-xs text-brand hover:underline">
                Se alle
              </Link>
            ) : null}
          </CardHeader>
          {recent_contracts.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              <EmptyState
                title="Ingen kontrakter endnu"
                description="Kontrakter synkroniseres automatisk fra GetAccept."
              />
            </div>
          ) : (
            /* `fill`: tabellen fylder kortets faste højde og ruller selv — se Table. */
            <Table fill>
              <thead>
                <tr>
                  <Th>Kontrakt</Th>
                  <Th className="text-right">Kontraktsum</Th>
                  <Th className="w-44">Faktureret</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {recent_contracts.map((contract) => (
                  <Tr key={contract.id}>
                    <Td>
                      {/* Samme link som paa /kontrakter, saa nummeret staar i adresselinjen. */}
                      {opensContracts ? (
                        <Link to={contractPath(contract)} className="font-medium hover:underline">
                          {contract.title}
                        </Link>
                      ) : (
                        <span className="font-medium">{contract.title}</span>
                      )}
                      <span className="block text-xs text-ink-muted">{contract.customer_name}</span>
                    </Td>
                    <Td className="text-right">
                      <Money value={contract.contract_sum_excl_vat} decimals={0} />
                    </Td>
                    <Td>
                      <Progress value={contract.billed_pct} />
                      <span className="mt-1 block text-xs text-ink-muted">
                        <Money value={contract.billed_excl_vat} decimals={0} />
                        {' · '}
                        <Percent value={contract.billed_pct} />
                      </span>
                    </Td>
                    <Td>
                      {isContractStatus(contract.status) ? (
                        <ContractStatusBadge status={contract.status} />
                      ) : (
                        <span className="text-xs">{contract.status}</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/*
          Samme opskrift som kortet ved siden af: `flex flex-col` her og `min-h-0 flex-1` på
          kroppen, så listen fylder rækkens højde og ruller det, der ikke er plads til.
          `min-h-0` er den væsentlige — uden den må et flex-barn ikke blive lavere end sit
          indhold, og tolv alarmer ville skubbe kortet ud af rækken i stedet for at rulle.

          `overflow-hidden` på kortet holder rullelisten inde bag de runde hjørner. Den sidder
          på disse to forekomster og ikke på `.card` i index.css — den sticky handlingsbjælke
          på /fakturering dør, hvis en forfader klipper.
        */}
        <Card className="flex flex-col overflow-hidden">
          <CardHeader className="flex shrink-0 items-center justify-between">
            <CardTitle>Alarmer</CardTitle>
            {seesAllAlerts ? (
              <Link to="/alarmer?recipient=all" className="text-xs text-brand hover:underline">
                Se alle
              </Link>
            ) : null}
          </CardHeader>
          <CardBody className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            {recent_alerts.length === 0 ? (
              /* Midt i kortet og ikke i toppen: højden er fast, så én linje foroven ville
                 efterlade netop det hul, den faste højde er til for at undgå. */
              <p className="flex h-full items-center justify-center text-xs text-ink-muted">
                Ingen åbne alarmer. Alt ser roligt ud.
              </p>
            ) : (
              recent_alerts.map((alert) => {
                const content = (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium leading-snug">{alert.title}</p>
                      <SeverityBadge severity={alert.severity} />
                    </div>
                    <p className="mt-1 text-xs text-ink-muted">
                      {formatRelative(alert.created_at)}
                    </p>
                  </>
                );
                return opensAlert(alert.recipient_id) ? (
                  <Link
                    key={alert.id}
                    to={`/alarmer/${alert.id}`}
                    className="block rounded-md border border-line p-3 hover:bg-canvas"
                  >
                    {content}
                  </Link>
                ) : (
                  <div key={alert.id} className="rounded-md border border-line p-3">
                    {content}
                  </div>
                );
              })
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Fakturering pr. måned</CardTitle>
            {/* Hele året, måned for måned - se demo/reports/billing-by-month.ts. */}
            {canSeeReport('billing-by-month') ? (
              <Link
                to="/rapporter/fakturering-pr-maaned"
                className="text-xs text-brand hover:underline"
              >
                Se alle
              </Link>
            ) : null}
          </CardHeader>
          <CardBody>
            {billing_by_month.length === 0 ? (
              <p className="text-xs text-ink-muted">Ingen fakturering opgjort endnu.</p>
            ) : (
              <>
                <BarChart
                  items={billing_by_month.map((month) => ({
                    label: month.month,
                    value: month.billed_excl_vat,
                    reference: month.budget_excl_vat,
                  }))}
                />
                <p className="mt-3 text-xs text-ink-muted">
                  {billing_by_month.some((month) => month.budget_excl_vat !== null)
                    ? 'Bogført ekskl. moms. Stregen er månedens budget i Business Central.'
                    : 'Bogført ekskl. moms. Business Central har intet budget for perioden.'}
                </p>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top 5 kunder i år</CardTitle>
          </CardHeader>
          <CardBody>
            {top_customers.length === 0 ? (
              <p className="text-xs text-ink-muted">Ingen kunder har betalt i år endnu.</p>
            ) : (
              <>
                <ul className="divide-y divide-line">
                  {top_customers.map((customer) => (
                    <li
                      key={customer.customer_id}
                      className="flex items-center justify-between gap-4 py-2 text-sm"
                    >
                      {opensCustomers ? (
                        <Link
                          to={customerPath({ id: customer.customer_id, cvr: null })}
                          className="truncate hover:underline"
                        >
                          {customer.name}
                        </Link>
                      ) : (
                        <span className="truncate">{customer.name}</span>
                      )}
                      <Money value={customer.paid_excl_vat} className="shrink-0" />
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-xs text-ink-muted">
                  Betalt i år ekskl. moms, efter betalingsdatoen i Business Central.
                </p>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}
